import { motion } from "framer-motion";
import { MessageCircle } from "lucide-react";
import { CATEGORIES, waProduct, WA_GENERAL } from "@/data/products";

const ProductCard = ({ p, wide, index }) => (
  <motion.a
    href={waProduct(p.name)}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={`product-quote-${p.slug}`}
    initial={{ opacity: 0, y: 32 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay: (index % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
    className={`group relative block border border-[#1A2235] bg-[#0A0E17] rounded-sm overflow-hidden hover:border-[#FF7A00]/60 transition-colors duration-300 ${
      wide ? "md:col-span-7" : "md:col-span-5"
    } col-span-1 sm:col-span-6`}
  >
    <div className={`overflow-hidden ${wide ? "aspect-[16/8]" : "aspect-[4/3]"}`}>
      <img
        src={p.img}
        alt={p.name}
        loading="lazy"
        style={{ objectPosition: p.pos }}
        className="w-full h-full object-cover saturate-[0.85] group-hover:saturate-100 group-hover:scale-[1.06] transition-[transform,filter] duration-700 ease-out"
      />
    </div>
    <div className="absolute inset-0 bg-gradient-to-t from-[#04070D] via-[#04070D]/45 to-transparent pointer-events-none" />
    <div className="absolute bottom-0 inset-x-0 p-5 md:p-6 flex items-end justify-between gap-4">
      <div>
        <h3 className="font-display text-lg md:text-2xl font-bold tracking-tight text-white">{p.name}</h3>
        <p className="text-[11px] md:text-xs text-[#8B95A5] mt-1">{p.spec}</p>
      </div>
      <span className="shrink-0 inline-flex items-center gap-2 bg-[#25D366] text-white text-xs font-semibold px-4 py-2.5 rounded-sm
        md:translate-y-3 md:opacity-0 md:group-hover:translate-y-0 md:group-hover:opacity-100 transition-[transform,opacity] duration-300">
        <MessageCircle size={14} /> Cotizar
      </span>
    </div>
  </motion.a>
);

export default function Catalogo() {
  return (
    <section id="materiales" className="border-t border-[#1A2235]" data-testid="catalogo-section">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-24 md:py-32">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 md:mb-20">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-[#FF7A00]" />
              <span className="font-mono-ind text-[11px] tracking-[0.25em] uppercase text-[#8B95A5]">Catálogo</span>
            </div>
            <h2 className="font-display font-black tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-6xl" style={{ fontWeight: 900 }}>
              Materiales de<br /><span className="text-outline">fabricación propia.</span>
            </h2>
          </div>
          <p className="text-sm text-[#8B95A5] max-w-xs leading-relaxed md:text-right">
            Sin precios en vitrina: cada proyecto se cotiza al momento.
            Toca cualquier material y te respondemos por WhatsApp el mismo día.
          </p>
        </div>

        {CATEGORIES.map((cat) => (
          <div key={cat.id} className="mb-20 md:mb-28 last:mb-0" data-testid={`category-${cat.id}`}>
            <div className="flex items-baseline justify-between border-b border-[#1A2235] pb-5 mb-8 md:mb-10">
              <div className="flex items-baseline gap-4 md:gap-6">
                <span className="font-mono-ind text-xs text-[#FF7A00]">{cat.index}</span>
                <h3 className="font-display text-2xl md:text-4xl font-black tracking-tight" style={{ fontWeight: 900 }}>{cat.name}</h3>
              </div>
              <span className="hidden sm:block text-xs text-[#8B95A5] font-mono-ind uppercase tracking-widest">{cat.tagline}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-5 md:gap-8">
              {cat.products.map((p, i) => (
                <ProductCard key={p.slug} p={p} index={i} wide={i === 0} />
              ))}
            </div>
          </div>
        ))}

        <div className="mt-16 border border-[#1A2235] rounded-sm p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 bg-[#0A0E17]">
          <div>
            <h4 className="font-display text-xl md:text-3xl font-bold tracking-tight">¿No ves el material que buscas?</h4>
            <p className="text-sm text-[#8B95A5] mt-2">Manejamos más calibres, largos y acabados. Pregunta sin compromiso.</p>
          </div>
          <a href={WA_GENERAL} target="_blank" rel="noopener noreferrer" data-testid="catalog-general-quote-button"
            className="shrink-0 inline-flex items-center gap-2 bg-[#FF7A00] hover:bg-[#E06A00] text-white font-semibold px-7 py-4 rounded-sm transition-colors duration-200">
            <MessageCircle size={18} /> Preguntar por WhatsApp
          </a>
        </div>
      </div>
    </section>
  );
}
